import React, { useMemo } from "react";

import { Box, Typography } from "@mui/material";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { makeStyles } from "tss-react/mui";

import {
  CalendarAppointment,
  STATUS_PALETTE,
  displayStatus,
  formatMinutes,
  minutesOfDay,
} from "./geometry";

type Props = {
  focusedDate: Date;
  appointments: CalendarAppointment[];
  onView: (id: string) => void;
  onSelectDate: (date: Date) => void;
  // Date of the appointment being encoded, shown as a placeholder chip.
  draftDate?: Date | null;
};

const WEEKDAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MAX_CHIPS = 3;

const AppointmentsMonthView: React.FC<Props> = ({
  focusedDate,
  appointments,
  onView,
  onSelectDate,
  draftDate,
}) => {
  const { classes, cx } = useStyles();
  const now = useMemo(() => new Date(), []);

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(focusedDate));
    const gridEnd = endOfWeek(endOfMonth(focusedDate));
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [focusedDate]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarAppointment[]>();
    appointments.forEach((appointment) => {
      const key = format(appointment.start, "yyyy-MM-dd");
      const list = map.get(key) ?? [];
      list.push(appointment);
      map.set(key, list);
    });
    map.forEach((list) =>
      list.sort((a, b) => minutesOfDay(a.start) - minutesOfDay(b.start))
    );
    return map;
  }, [appointments]);

  return (
    <Box className={classes.root}>
      <div className={classes.weekdayRow}>
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={classes.weekdayCell}>
            {label}
          </div>
        ))}
      </div>
      <div className={classes.grid}>
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const dayAppointments = byDay.get(key) ?? [];
          const inMonth = isSameMonth(day, focusedDate);
          const visible = dayAppointments.slice(0, MAX_CHIPS);
          const overflow = dayAppointments.length - visible.length;
          return (
            <div
              key={key}
              className={cx(classes.cell, !inMonth && classes.cellMuted)}
              onClick={() => onSelectDate(day)}
            >
              <div className={classes.cellHeader}>
                <span
                  className={cx(
                    classes.dayNumber,
                    isToday(day) && classes.dayNumberToday
                  )}
                >
                  {format(day, "d")}
                </span>
              </div>
              <div className={classes.chips}>
                {draftDate && isSameDay(day, draftDate) && (
                  <div className={classes.draftChip}>New appointment</div>
                )}
                {visible.map((appointment) => {
                  const palette =
                    STATUS_PALETTE[displayStatus(appointment, now)];
                  return (
                    <button
                      key={appointment.id}
                      type="button"
                      className={classes.chip}
                      style={{
                        backgroundColor: palette.bg,
                        color: palette.text,
                        borderLeft: `3px solid ${palette.border}`,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onView(appointment.id);
                      }}
                    >
                      <span className={classes.chipTime}>
                        {formatMinutes(minutesOfDay(appointment.start))}
                      </span>
                      <span className={classes.chipName}>
                        {appointment.clientName ?? "Appointment"}
                      </span>
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <Typography className={classes.more}>
                    +{overflow} more
                  </Typography>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #DEE6F0",
    borderRadius: 8,
    overflow: "hidden",
  },
  weekdayRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    borderBottom: "1px solid #DEE6F0",
  },
  weekdayCell: {
    padding: "8px 0",
    textAlign: "center",
    fontSize: 11,
    fontWeight: 700,
    color: "#7D8797",
    letterSpacing: 0.5,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gridAutoRows: "minmax(104px, 1fr)",
  },
  cell: {
    borderRight: "1px solid #EDF0F5",
    borderBottom: "1px solid #EDF0F5",
    padding: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    cursor: "pointer",
    minWidth: 0,
    "&:hover": {
      backgroundColor: "rgba(61,109,240,0.04)",
    },
  },
  cellMuted: {
    backgroundColor: "#FAFBFD",
    "& $dayNumber": {
      color: "#B8BEC9",
    },
  },
  cellHeader: {
    display: "flex",
    justifyContent: "flex-end",
  },
  dayNumber: {
    fontSize: 12,
    fontWeight: 700,
    color: "#363E45",
    width: 22,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dayNumberToday: {
    backgroundColor: "#3D6DF0",
    color: "#fff",
    borderRadius: "50%",
  },
  chips: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  chip: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    width: "100%",
    border: "none",
    borderRadius: 4,
    padding: "1px 4px",
    fontSize: 11,
    cursor: "pointer",
    textAlign: "left",
    overflow: "hidden",
  },
  chipTime: {
    fontWeight: 700,
    flexShrink: 0,
  },
  chipName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  more: {
    fontSize: 10,
    fontWeight: 600,
    color: "#7D8797",
    paddingLeft: 4,
  },
  draftChip: {
    width: "100%",
    borderRadius: 4,
    padding: "1px 4px",
    fontSize: 11,
    fontWeight: 700,
    color: "#1D3FA8",
    backgroundColor: "rgba(61,109,240,0.15)",
    border: "1px dashed #3D6DF0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

export default AppointmentsMonthView;
